import React, { Component } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import styled, { withTheme } from 'styled-components';
import { Grid, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { destroyCurrentUserSession } from 'services/users/duck';
import { push } from 'connected-react-router';
import fastMemoize from 'fast-memoize';
import DebounceInputChange from 'hoc/DebounceInputChange';
import StyledReactTable from 'components/molecules/StyledReactTable';
import CreateNewButton from 'components/atoms/buttons/CreateNewButton';
import Pagination from 'components/molecules/tables/components/Pagination';

const DEFAULT_PAGE = 0;
const DEFAULT_PAGE_SIZE = 10;

const DebouncedInput = DebounceInputChange(500)(props => <input {...props} />);

const generateFilterValue = (filter, row) => {
    const filterValue = row[filter.id];
    return filterValue;
};

const AddDashboardsButton = styled.div`
    font-size: 10px;
    margin-right: 10px;
    padding: 5px;
    background: ${props => props.theme.colors.lightgray};
    border: 1px solid #AFAFAF;
    user-select: none;
    cursor: pointer;

    &:hover {
        border: 1px solid ${props => props.theme.colors.darkgray};
        background: ${props => props.theme.colors.primary};
        color: #fff
    }
`;

const LinkWrapper = styled.div`
    padding: 8px 0px 8px 20px;
    font-size: 15px;
    border: solid 1px #E8E8E880;
    border-left: none;
    border-right: none;
`;

const memoizeFilterValue = fastMemoize(generateFilterValue);

const HomeHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #F1F1F1;
    padding: 8px 0px 8px 20px;
    border-radius: 5px 5px 0 0;
    text-transform: uppercase;
    font-size: 16px;
    border-bottom: solid 1px #AFAFAF;
`;

const ContentWrapper = styled.div`
    border: solid 1px #AFAFAF;
    overflow: show;
    border-radius: 5px;
    margin-bottom: 15px;
`;

class AdminHomeContainer extends Component {
    constructor(props) {
        super(props);
        this.getColumns = this.getColumns.bind(this);
        this.activeSearch = false;
        this.state = {
            showPagination: false,
            filtered: [],
            list: null,
        };

        this.AdminHomeTableInstance = React.createRef();
        this.setActiveSearch = this.setActiveSearch.bind(this);
    }

    static getDerivedStateFromProps(props, state) {
        const propList = get(props, 'userEntities', []);
        const mergeList = [];

        if (propList !== state.list) {
            let showPagination = state.showPagination;

            if (!state.showPagination && propList.length > DEFAULT_PAGE_SIZE) {
                showPagination = true;
            }

            mergeList.push({
                list: propList,
                showPagination
            });
        }

        if (mergeList.length) {
            return Object.assign.apply(null, [
                {},
                state
            ].concat(mergeList));
        }
        return null;
    }

    shouldComponentUpdate() {
        return !this.activeSearch;
    }

    setActiveSearch(value) {
        this.activeSearch = value;
    }

    getAdminHomeTableInstance() {
        if (!this.AdminHomeTableInstance) {
            console.warn('React Table - No instance'); // eslint-disable-line
        }

        return this.AdminHomeTableInstance;
    }

    linkToEditEntity(cell, row) {
        return (
            <Link to={`/admin/entities/${row.id}/edit`} title="Edit entity" style={{ marginLeft: 15 }}>
                <span className="icon-edit" />
            </Link>
        );
    }

    entityNameLink(cell, row) {
        return (
            <Link to={`/admin/entities/${row.id}`}>
                {row.label}
            </Link>
        );
    }

    entityDashboardLink(cell, row) {
        return (
            <Link to={`/dashboard/${row.id}/summary`} target="_blank" title="Go to dashboard">
                <span className="icon-open" />
            </Link>
        );
    }

    onFilteredChangeCustom(value, accessor) {
        let filtered = this.state.filtered;

        const tempFilters = filtered.reduce((prev, filter) => {
            prev[filter.id] = filter.value;
            return prev;
        }, {});

        tempFilters[accessor] = value;

        filtered = Object.keys(tempFilters).map((key) => {
            if (tempFilters[key] === null) {
                return false;
            }
            return { id: key, value: tempFilters[key] };
        }).filter(item => item);

        const instance = this.getAdminHomeTableInstance().current;
        let page = this.state.page;
        if (get(instance, 'state.sortedData', []).length < this.state.page * this.state.pageSize) {
            page = DEFAULT_PAGE;
        }

        this.setState({ filtered, page });
    }

    getColumns() {
        return [
            {
                Header: '',
                Cell: props => (
                    <React.Fragment>
                        {this.entityDashboardLink(null, props.original)}
                        {this.linkToEditEntity(null, props.original)}
                    </React.Fragment>
                ),
                maxWidth: 60,
                resizable: false,
                sortable: false
            }, {
                Header: 'LABEL',
                Cell: props => this.entityNameLink(null, props.original),
                accessor: 'label',
                filterable: true,
                Filter: ({ filter, onChange }) => (
                    <DebouncedInput
                        type="text"
                        className="filter text-filter form-control entityName-filter"
                        onChange={value => onChange(value)}
                        onFocus={() => this.setActiveSearch(true)}
                        onBlur={() => this.setActiveSearch(false)}
                        placeholder="Search Favorites"
                        style={{ height: 38, borderColor: 'hsl(0,0%,80%)' }}
                        value={filter ? filter.value : ''} />
                ),
                filterMethod: (filter, row) => {
                    const filterValue = memoizeFilterValue(filter, row);

                    return filterValue.toLowerCase().includes(filter.value.toLowerCase());
                }
            }
        ];
    }


    render() {
        const { userEntities, userId } = this.props;
        const data = userEntities.map(row => ({
            ...row
        }));

        return (
            <Grid fluid>
                <Row className="hidden-print">
                    <Col sm={12} md={8}>
                        <ContentWrapper>
                            <HomeHeader>Favorite Dashboards <AddDashboardsButton onClick={() => this.props.push(`/admin/users/${userId}/edit`)}>Add Dashboards</AddDashboardsButton></HomeHeader>
                            {userEntities.length ? (
                                <StyledReactTable
                                    data={data}
                                    ref={this.AdminHomeTableInstance}
                                    defaultPageSize={DEFAULT_PAGE_SIZE}
                                    minRows={DEFAULT_PAGE_SIZE}
                                    filtered={this.state.filtered}
                                    sorted={this.state.sorted}
                                    page={this.state.page}
                                    pageSize={this.state.pageSize}
                                    onFilteredChange={(filtered, column, value) => {
                                        this.onFilteredChangeCustom(value, column.id || column.accessor);
                                    }}
                                    onSortedChange={sorted => this.setState({ sorted })}
                                    onPageChange={page => this.setState({ page })}
                                    onPageSizeChange={(pageSize, page) => this.setState({
                                        page, pageSize
                                    })}
                                    getTheadThProps={() => ({
                                        style: {
                                            display: 'none'
                                        }
                                    })}
                                    getTheadFilterThProps={() => ({
                                        style: {
                                            overflow: 'inherit' // allows react-select to work
                                        }
                                    })}
                                    columns={this.getColumns()}
                                    defaultSorted={[{
                                        id: 'label', desc: false
                                    }]}
                                    className="-highlight"
                                    showPagination={this.state.showPagination}
                                    PaginationComponent={Pagination}
                                />
                            ) : (
                                <LinkWrapper>
                                            You do not have any dashboards on your favorites list.
                                    <Link to={`/admin/users/${userId}/edit`} title="Go to Your Profile">Add Dashboards</Link>
                                </LinkWrapper>
                            )}
                        </ContentWrapper>
                    </Col>
                    <Col sm={12} md={4}>
                        <CreateNewButton type='Entity' icon='entities' onClick={() => this.props.push('/admin/entities/create')} />
                        <CreateNewButton type='User' icon='user' onClick={() => this.props.push('/admin/users/create')} />
                        <CreateNewButton type='Brand' icon='brands' onClick={() => this.props.push('/admin/brands/create')} />
                    </Col>
                </Row>
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    userEntities: get(state, 'users.currentUser.userEntities', []),
    isOkta: get(state, 'users.currentUser.isOkta', false),
    userId: get(state, 'users.currentUser.id', null),
    userScope: get(state, 'users.currentUser.scope', [])
});

export default connect(mapStateToProps, { destroyCurrentUserSession, push })(withTheme(AdminHomeContainer));



// WEBPACK FOOTER //
// ./src/containers/home/AdminHomeContainer.js